import { BookOpen, LayoutGrid, SlidersHorizontal } from 'lucide-react';
import { createElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
  AppShell,
  type ContextEntry,
  type NavGroup,
  Sidebar,
  TopBar,
} from '@/components/layout/AppShell';
import { ICON_SIZE } from '@/components/ui/iconSize';
import { StepMark } from '@/components/ui/navigation';
import { can } from '@/config/access';
import { BRANDS_PATH, brandTabPath, CAMPAIGNS_PATH, campaignSectionPath, settingsPath } from '@/config/paths';
import { type AppRoute, CAMPAIGN_SECTION_ROUTES, NAV_GROUPS } from '@/config/routes';
import { signOut } from '@/features/auth/session';
import { useActiveMembership, useSession } from '@/features/auth/useSession';
import { useLiveMembership } from '@/features/auth/useSignIn';
import { BRAND_TAB_ICONS, BRAND_TABS } from '@/features/brandHub/brand/brand';
import { useBrand } from '@/features/brandHub/brand/useBrands';
import { useBrandProductCount } from '@/features/brandHub/products/useProducts';
import { useCampaign } from '@/features/campaigns/campaign/useCampaigns';
import { useMatchedRoute, usePageHeading } from '@/features/layout/usePageHeading';
import {
  useApplyBrandColors,
  useOrganization,
  useWatchOrganizationBranding,
} from '@/features/organization/useOrganization';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useShellChrome } from '@/hooks/useShellChrome';
import { useTheme } from '@/hooks/useTheme';
import { activeLanguage, LANGUAGE_NAMES, SUPPORTED_LANGUAGES } from '@/i18n';

const LANGUAGE_OPTIONS = SUPPORTED_LANGUAGES.map((code) => ({
  code,
  name: LANGUAGE_NAMES[code],
}));

const GROUP_ICONS: Record<string, ReactNode> = {
  brandHub: <BookOpen size={ICON_SIZE.md} />,
  workspace: <LayoutGrid size={ICON_SIZE.md} />,
};

/** Shows the sidebar, the top bar and the page heading around the open route. */
export function AppChrome({ children }: { children: ReactNode }) {
  const { t, i18n: instance } = useTranslation();
  const language = activeLanguage(instance.language);
  const navigate = useNavigate();
  const session = useSession();
  const membership = useActiveMembership();
  const { theme, setTheme } = useTheme();
  const heading = usePageHeading();
  const { collapsed, toggleCollapsed, drawerOpen, openDrawer, closeDrawer } = useShellChrome();

  const organization = useOrganization();
  useWatchOrganizationBranding();
  useApplyBrandColors(
    organization.data?.primaryColor,
    organization.data?.secondaryColor,
  );

  useLiveMembership();

  const [openGroups, setOpenGroups] = useLocalStorage<Record<string, boolean>>(
    'edify_nav_groups',
    { brandHub: true, workspace: true },
  );

  const { params } = useMatchedRoute();
  const { campaignId } = params;
  const { campaign } = useCampaign(campaignId);

  const brandSlug = params.brandSlug ?? '';
  const { brand } = useBrand(brandSlug);
  const productCount = useBrandProductCount(brandSlug || null);

  const allowed = (route: AppRoute) => can(membership, route.action);

  const groups: NavGroup[] = NAV_GROUPS.map((group) => ({
    key: group.key,
    label: t(group.labelKey),
    icon: GROUP_ICONS[group.key],
    open: openGroups[group.key] ?? true,
    onToggle: () =>
      setOpenGroups((current) => ({ ...current, [group.key]: !(current[group.key] ?? true) })),
    items: group.routes.filter(allowed).map((route) => ({
      to: route.path,
      label: t(route.labelKey),
      icon: route.icon,
      end: route.end,
    })),
  })).filter((group) => group.items.length > 0);

  // Ticks the brief step when the campaign is complete.
  const contextEntries: ContextEntry[] = CAMPAIGN_SECTION_ROUTES.filter(allowed).flatMap((route, step) => {
    const item = {
      to: campaignSectionPath(campaignId ?? '', route.path),
      label: t(route.labelKey),
      icon: <StepMark state={route.path === 'brief' && campaign?.status === 'complete' ? 'complete' : 'pending'} index={step} />,
    };
    return route.groupLabelKey
      ? [
          {
            label: t(route.groupLabelKey),
            icon: <SlidersHorizontal size={ICON_SIZE.sm} />,
            isLabel: true as const,
          },
          item,
        ]
      : [item];
  });

  const brandEntries: ContextEntry[] = BRAND_TABS.map((tab) => ({
    to: brandTabPath(brandSlug, tab),
    icon: createElement(BRAND_TAB_ICONS[tab], { size: ICON_SIZE.sm }),
    label:
      tab === 'products'
        ? t('brandHub.brand.productsTab', { count: productCount })
        : t(`brandHub.brand.tabs.${tab}`),
  }));

  const context =
    campaignId && campaign
      ? {
          groupKey: 'workspace',
          kindLabel: t('campaign.kind'),
          name: campaign.name,
          backTo: CAMPAIGNS_PATH,
          backLabel: t('campaigns.allCampaigns'),
          entries: contextEntries,
        }
      : brandSlug && brand
        ? {
            groupKey: 'brandHub',
            kindLabel: t('brandHub.brand.title'),
            name: brand.name,
            backTo: BRANDS_PATH,
            backLabel: t('nav.brands'),
            entries: brandEntries,
          }
        : undefined;

  return (
    <AppShell
      collapsed={collapsed}
      heading={heading}
      sidebar={
        <Sidebar
          groups={groups}
          collapsed={collapsed}
          onToggleCollapse={toggleCollapsed}
          drawerOpen={drawerOpen}
          onCloseDrawer={closeDrawer}
          ariaLabel={t('app.name')}
          labels={{ collapse: t('common.collapseSidebar'), expand: t('common.expandSidebar') }}
          context={context}
        />
      }
      topBar={
        <TopBar
          onOpenDrawer={openDrawer}
          drawerOpen={drawerOpen}
          theme={theme}
          onThemeChange={setTheme}
          language={language}
          languages={LANGUAGE_OPTIONS}
          onLanguageChange={(code) => void instance.changeLanguage(code)}
          user={{
            name: session?.name ?? '',
            email: session?.email ?? '',
            roleLabel: t(`settings.role.${membership?.role ?? 'member'}`),
          }}
          isAdmin={membership?.role === 'admin'}
          onOpenProfile={() => navigate(settingsPath('profile'))}
          onOpenOrganization={() => navigate(settingsPath('organization'))}
          onSignOut={() => signOut()}
          labels={{
            openMenu: t('common.openMenu'),
            language: t('common.language'),
            theme: t('common.theme'),
            themeLight: t('common.themeLight'),
            themeDark: t('common.themeDark'),
            themeSystem: t('common.themeSystem'),
            profileMenu: t('common.profileMenu'),
            profile: t('nav.profile'),
            organization: t('nav.organization'),
            signOut: t('common.signOut'),
          }}
        />
      }
    >
      {children}
    </AppShell>
  );
}
